import { AlertCircle, Mail, Clock, CreditCard, ExternalLink, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { AccessBlockReason } from "../hooks/useAccessControl";

interface AccessBlockBannerProps {
  blockReason: AccessBlockReason;
  blockMessage: string;
}

export default function AccessBlockBanner({ blockReason, blockMessage }: AccessBlockBannerProps) {
  if (!blockReason) return null;

  // Email Not Verified Banner
  if (blockReason === "EMAIL_NOT_VERIFIED") {
    return (
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-400 rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-amber-900 mb-2 flex items-center gap-2">
              <AlertCircle className="w-6 h-6" />
              กรุณายืนยันอีเมลเพื่อเริ่มใช้งาน
            </h3>
            <p className="text-amber-800 mb-4">
              เพื่อความปลอดภัยและการใช้งานที่สมบูรณ์ กรุณายืนยันอีเมลของคุณก่อนใช้งานฟีเจอร์ต่างๆ
            </p>
            
            <div className="bg-white/60 rounded-lg p-4 mb-4">
              <p className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                วิธีการยืนยันอีเมล:
              </p>
              <ol className="space-y-2 text-sm text-amber-800">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-amber-600">1.</span>
                  <span>เปิดอีเมลที่คุณใช้สมัคร</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-amber-600">2.</span>
                  <span>ค้นหาอีเมลจาก EduMi (ตรวจสอบใน Spam ด้วย)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-amber-600">3.</span>
                  <span>คลิกลิงก์ยืนยันในอีเมล</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-amber-600">4.</span>
                  <span>รีเฟรชหน้านี้เพื่อเริ่มใช้งาน</span>
                </li>
              </ol>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/verify-email"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <Mail className="w-5 h-5" />
                ส่งอีเมลยืนยันอีก
              </Link>
              <a
                href="https://line.me/R/ti/p/@edumi"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <ExternalLink className="w-5 h-5" />
                ติดต่อ LINE @EduMi
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Subscription Expired Banner
  if (blockReason === "SUBSCRIPTION_EXPIRED") {
    return (
      <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-400 rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-red-900 mb-2 flex items-center gap-2">
              <Clock className="w-6 h-6" />
              แพ็คเก็จของคุณหมดอายุแล้ว
            </h3>
            <p className="text-red-800 mb-4">
              แพ็คเก็จการใช้งานของคุณหมดอายุแล้ว กรุณาต่ออายุเพื่อใช้งานฟีเจอร์ต่างๆ ต่อ
            </p>
            
            <div className="bg-white/60 rounded-lg p-4 mb-4">
              <p className="font-semibold text-red-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                ประโยชน์ที่คุณจะได้รับเมื่อต่ออายุ:
              </p>
              <ul className="space-y-2 text-sm text-red-800">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>สร้างและจัดการคอร์สเรียนได้ไม่จำกัด</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>อัปโหลดวิดีโอและเนื้อหาการสอน</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>เข้าถึงระบบวิเคราะห์และรายงาน</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>รับการสนับสนุนจากทีมงาน</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/subscription/renew"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <CreditCard className="w-5 h-5" />
                ต่ออายุแพ็คเก็จตอนนี้
              </Link>
              <a
                href="https://line.me/R/ti/p/@edumi"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <ExternalLink className="w-5 h-5" />
                สอบถามเพิ่มเติม LINE @EduMi
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Payment Pending Banner
  if (blockReason === "PAYMENT_PENDING") {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-400 rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-blue-900 mb-2 flex items-center gap-2">
              <Clock className="w-6 h-6" />
              รอการอนุมัติการชำระเงิน
            </h3>
            <p className="text-blue-800 mb-4">
              ระบบกำลังตรวจสอบการชำระเงินของคุณ โดยปกติจะใช้เวลาประมาณ 5-30 นาที
            </p>
            
            <div className="bg-white/60 rounded-lg p-4 mb-4">
              <p className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                ขั้นตอนต่อไป:
              </p>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-blue-600">•</span>
                  <span>Admin กำลังตรวจสอบหลักฐานการชำระเงินของคุณ</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-blue-600">•</span>
                  <span>เมื่อได้รับการอนุมัติ คุณจะสามารถใช้งานได้ทันที</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-blue-600">•</span>
                  <span>หากรอเกิน 30 นาที กรุณาติดต่อ LINE @EduMi</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <Clock className="w-5 h-5" />
                รีเฟรชหน้า
              </button>
              <a
                href="https://line.me/R/ti/p/@edumi"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <ExternalLink className="w-5 h-5" />
                ติดต่อทันที LINE @EduMi
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default fallback
  return (
    <div className="bg-gradient-to-r from-gray-50 to-slate-50 border-2 border-gray-400 rounded-2xl shadow-lg p-6 mb-6">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-white" />
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <AlertCircle className="w-6 h-6" />
            ไม่สามารถใช้งานได้ในขณะนี้
          </h3>
          <p className="text-gray-800 mb-4">{blockMessage}</p>
          <a
            href="https://line.me/R/ti/p/@edumi"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <ExternalLink className="w-5 h-5" />
            ติดต่อ LINE @EduMi
          </a>
        </div>
      </div>
    </div>
  );
}
